import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  useGetAccountActivationContextQuery,
  useProcessAccountActivationMutation,
} from '../api/authApi';
import { clearPasswordResetCooldowns } from '../hooks/useRestorePassword';
import { extractErrorMessage } from '../../../utils/errorUtils';

const MIN_PASSWORD_LENGTH = 10;

const pageContainerSx = {
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'background.default',
};

type ChangePasswordPageProps = {
  axprtl?: string;
};

export const ChangePasswordPage = ({ axprtl }: ChangePasswordPageProps) => {
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const {
    data: tokenData,
    isLoading: isValidating,
    isError: isTokenError,
    error: tokenError,
  } = useGetAccountActivationContextQuery(axprtl ?? '', { skip: !axprtl });

  const [completeAccountSetup, { isLoading: isChanging, error: changeError, isSuccess }] =
    useProcessAccountActivationMutation();
  const [sendTwoFactorCode, { isLoading: isSendingCode, error: sendCodeError }] =
    useProcessAccountActivationMutation();

  const needsPassword = tokenData?.NeedPasswordChange ?? false;
  const needsPhone = tokenData?.NeedToAddPhoneNumber ?? false;
  const passwordsMatch = !confirmPassword || password === confirmPassword;
  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const passwordIsWhitespace = password.length > 0 && password.trim().length === 0;
  const passwordIsValid =
    !needsPassword ||
    (!passwordIsWhitespace &&
      password.length >= MIN_PASSWORD_LENGTH &&
      confirmPassword.length >= MIN_PASSWORD_LENGTH &&
      password === confirmPassword);
  const phoneIsValid = !needsPhone || (phoneNumber.length > 0 && codeSent && smsCode.length > 0);
  const isFormValid = passwordIsValid && phoneIsValid;

  const handleSendCode = async () => {
    if (!axprtl || !phoneNumber) return;
    try {
      await sendTwoFactorCode({ operation: 'sendCode', token: axprtl, phoneNumber }).unwrap();
      setCodeSent(true);
    } catch (err) {
      console.error('Verification code request failed:', err);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isFormValid || !axprtl) return;

    try {
      await completeAccountSetup({
        operation: 'complete',
        token: axprtl,
        password: needsPassword ? password : undefined,
        confirmPassword: needsPassword ? confirmPassword : undefined,
        phoneNumber: needsPhone ? phoneNumber : undefined,
        smsCode: needsPhone ? smsCode : undefined,
      }).unwrap();

      clearPasswordResetCooldowns();
    } catch (err) {
      console.error('Password change failed:', err);
    }
  };

  if (isValidating) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!axprtl || isTokenError || (tokenData && !tokenData.Success)) {
    return (
      <Box sx={pageContainerSx}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
          <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
            Link Expired
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {extractErrorMessage(
              tokenError,
              'Your password reset link is invalid or has expired. Please request a new one.'
            )}
          </Alert>
          <Button fullWidth variant="contained" onClick={() => void navigate({ to: '/login' })}>
            Back to Sign In
          </Button>
        </Paper>
      </Box>
    );
  }

  if (isSuccess) {
    return (
      <Box sx={pageContainerSx}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
          <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
            Account Setup Complete
          </Typography>
          <Alert severity="success" sx={{ mb: 3 }}>
            Your account has been configured successfully. You can now sign in.
          </Alert>
          <Button fullWidth variant="contained" onClick={() => void navigate({ to: '/login' })}>
            Sign In
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
          {needsPhone ? 'Complete Account Setup' : 'Set New Password'}
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          {needsPassword && needsPhone
            ? 'Set a new password and confirm your phone number before signing in.'
            : needsPassword
              ? 'You are required to set a new password before signing in.'
              : 'Confirm your phone number to enable two-factor authentication.'}
        </Typography>

        {tokenData && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {tokenData.Username && (
              <TextField
                label="Username"
                value={tokenData.Username}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
            )}
            {tokenData.Email && (
              <TextField
                label="Email"
                value={tokenData.Email}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
            )}
          </Box>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {needsPassword && (
            <>
              <TextField
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                autoComplete="new-password"
                error={passwordTooShort || passwordIsWhitespace}
                helperText={
                  passwordIsWhitespace
                    ? 'Password cannot contain only spaces.'
                    : passwordTooShort
                      ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
                      : undefined
                }
                FormHelperTextProps={{ sx: { mx: 0 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm Password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                required
                autoComplete="new-password"
                error={!passwordsMatch}
                helperText={!passwordsMatch ? 'Passwords do not match' : undefined}
                FormHelperTextProps={{ sx: { mx: 0 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirm((v) => !v)}
                        edge="end"
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          {needsPhone && (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    setSmsCode('');
                    setCodeSent(false);
                  }}
                  placeholder="+123456789012"
                  autoComplete="tel"
                  fullWidth
                  required
                />
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => void handleSendCode()}
                  disabled={!phoneNumber || isSendingCode}
                  sx={{ minWidth: 120, height: 56 }}
                >
                  {isSendingCode ? (
                    <CircularProgress size={22} />
                  ) : codeSent ? (
                    'Resend'
                  ) : (
                    'Send Code'
                  )}
                </Button>
              </Box>

              {codeSent && (
                <TextField
                  label="SMS Code"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  autoComplete="one-time-code"
                  fullWidth
                  required
                />
              )}
            </>
          )}

          {sendCodeError && (
            <Alert severity="error">
              {extractErrorMessage(sendCodeError, 'Failed to send verification code.')}
            </Alert>
          )}

          {changeError && (
            <Alert severity="error">
              {extractErrorMessage(changeError, 'An error occurred. Please try again.')}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!isFormValid || isChanging || isSendingCode}
            sx={{ py: 1.5 }}
          >
            {isChanging ? (
              <CircularProgress size={24} color="inherit" />
            ) : needsPhone ? (
              'Complete Account Setup'
            ) : (
              'Set New Password'
            )}
          </Button>

          <Button variant="text" fullWidth onClick={() => void navigate({ to: '/login' })}>
            Back to Sign In
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
